import axios from 'axios';

// Use relative URL so Vite proxy can forward to backend on port 5000
const BACKEND_URL = import.meta.env.VITE_APP_API_URL + '/api';

const HallOfFameService = {
  // Get leaderboard data with all participants
  getLeaderboard: async (tier?: string, page: number = 0, size: number = 50): Promise<any> => {
    try {
      // Enforce backend maximum size limit of 200
      const validSize = Math.min(size, 200);

      const params: any = {
        page: page.toString(),
        size: validSize.toString()
      };

      if (tier && tier !== 'All' && tier !== 'all') {
        params.tier = tier;
      }

      const queryString = new URLSearchParams(params).toString();
      console.log('📊 Fetching leaderboard:', `/api/leaderboard?${queryString}`);

      const response = await axios.get(`${BACKEND_URL}/leaderboard?${queryString}`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch leaderboard:', error);
      throw new Error(error.response?.data?.message || 'Failed to connect to backend on port 5000. Make sure Spring Boot is running.');
    }
  },

  // Create participant
  createParticipant: async (data: {
    name: string;
    email: string;
    socialHandle?: string;
    avatarUrl?: string;
  }): Promise<any> => {
    try {
      const response = await axios.post(`${BACKEND_URL}/participants`, data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to create participant:', error);
      if (error.response?.status === 409) {
        throw new Error('Email already exists');
      }
      throw new Error(error.response?.data?.message || 'Failed to create participant');
    }
  },

  // Submit stock picks for a participant
  submitPicks: async (participantId: string, picks: { symbol: string; entryPrice?: number }[]): Promise<any> => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/leaderboard/participants/${participantId}/picks`,
        { picks }
      );
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to submit picks:', error);
      if (error.response?.status === 403) {
        throw new Error('Portfolio is already locked');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid picks - must submit exactly 5 unique tickers');
      }
      throw new Error(error.response?.data?.message || 'Failed to submit picks');
    }
  },

  // Get individual participant details
  getParticipantDetails: async (participantId: string): Promise<any> => {
    try {
      console.log('🔍 Fetching participant details for ID:', participantId);
      const response = await axios.get(`${BACKEND_URL}/leaderboard/participants/${participantId}`);
      console.log('🔍 Participant details response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch participant details:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch participant');
    }
  },


  // Get participant performance history
  getParticipantPerformance: async (participantId: string): Promise<any> => {
    try {
      console.log('🔍 Fetching performance for participant ID:', participantId);
      const response = await axios.get(`${BACKEND_URL}/leaderboard/participants/${participantId}/performance`);
      console.log('🔍 Performance response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch participant performance:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch participant performance');
    }
  },

  // Get participant by email
  getParticipantByEmail: async (email: string): Promise<any> => {
    try {
      const response = await axios.get(`${BACKEND_URL}/participants/by-email/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // Email not found
      }
      console.error('❌ Failed to check email:', error);
      throw new Error(error.response?.data?.message || 'Failed to check email');
    }
  },

  // Get all participants
  getAllParticipants: async (): Promise<any> => {
    try {
      const response = await axios.get(`${BACKEND_URL}/participants`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch participants:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch participants');
    }
  },
};

export default HallOfFameService;

